import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Badge, ProgressBar } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import { toast } from 'react-toastify'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend, LineChart, Line } from 'recharts'

interface DashboardStats {
  totalTickets: number
  openTickets: number
  inProgressTickets: number
  resolvedTickets: number
  closedTickets: number
  myTickets: number
  assignedToMe: number
  pendingCustomer: number
  highPriority: number
  urgentTickets: number
  avgResolutionTime: string
  slaCompliance: number
  agentPerformance?: Array<{ name: string; tickets: number }>
  trendsOverTime?: Array<{ name: string; value: number }>
  userActivity?: Array<{ name: string; tickets: number }>
  commonIssues?: Array<{ name: string; count: number }>
}

export const DashboardPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      const ticketsResponse = await api.get('/tickets')
      const tickets = ticketsResponse.data.data || []
      
      // Calculate comprehensive stats
      const totalTickets = tickets.length
      const openTickets = tickets.filter((t: any) => t.status === 'OPEN').length
      const inProgressTickets = tickets.filter((t: any) => t.status === 'IN_PROGRESS').length
      const resolvedTickets = tickets.filter((t: any) => t.status === 'RESOLVED').length
      const closedTickets = tickets.filter((t: any) => t.status === 'CLOSED').length
      const pendingCustomer = tickets.filter((t: any) => t.status === 'PENDING_CUSTOMER').length
      const highPriority = tickets.filter((t: any) => t.priority === 'High' || t.priority === 'Urgent').length
      const urgentTickets = tickets.filter((t: any) => t.priority === 'Urgent').length
      
      let myTickets = 0
      let assignedToMe = 0
      
      if (user?.role === 'REQUESTER') {
        myTickets = tickets.filter((t: any) => t.requesterId === user?.id).length
      } else if (user?.role === 'AGENT') {
        assignedToMe = tickets.filter((t: any) => t.assigneeId === user?.id).length
        myTickets = assignedToMe
      } else if (user?.role === 'ADMIN') {
        myTickets = totalTickets
      }
      
      // Calculate SLA compliance (mock calculation)
      const slaCompliance = totalTickets > 0 ? Math.round((resolvedTickets / totalTickets) * 100) : 0
      
      // Calculate agent performance (group by assignee)
      const agentMap = new Map<string, number>()
      tickets.forEach((t: any) => {
        if (t.assignee?.name) {
          const currentCount = agentMap.get(t.assignee.name) || 0
          agentMap.set(t.assignee.name, currentCount + 1)
        }
      })
      const agentPerformance = Array.from(agentMap.entries())
        .map(([name, tickets]) => ({ name, tickets }))
        .sort((a, b) => b.tickets - a.tickets)
        .slice(0, 5) // Top 5 agents
      
      // Calculate trends over time (last 7 days)
      const now = new Date()
      const trendsMap = new Map<string, number>()
      
      // Initialize last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now)
        date.setDate(date.getDate() - i)
        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' })
        trendsMap.set(dayName, 0)
      }
      
      // Count tickets by day
      tickets.forEach((t: any) => {
        const ticketDate = new Date(t.createdAt)
        const daysDiff = Math.floor((now.getTime() - ticketDate.getTime()) / (1000 * 60 * 60 * 24))
        if (daysDiff < 7) {
          const dayName = ticketDate.toLocaleDateString('en-US', { weekday: 'short' })
          const currentCount = trendsMap.get(dayName) || 0
          trendsMap.set(dayName, currentCount + 1)
        }
      })
      
      const trendsOverTime = Array.from(trendsMap.entries()).map(([name, value]) => ({ name, value }))
      
      // Calculate user activity (group by requester)
      const userMap = new Map<string, number>()
      tickets.forEach((t: any) => {
        if (t.requester?.name) {
          const currentCount = userMap.get(t.requester.name) || 0
          userMap.set(t.requester.name, currentCount + 1)
        }
      })
      const userActivity = Array.from(userMap.entries())
        .map(([name, tickets]) => ({ name, tickets }))
        .sort((a, b) => b.tickets - a.tickets)
        .slice(0, 5) // Top 5 users
      
      // Calculate common issues (group by category)
      const categoryMap = new Map<string, number>()
      tickets.forEach((t: any) => {
        const categoryName = t.category?.name || 'Uncategorized'
        const currentCount = categoryMap.get(categoryName) || 0
        categoryMap.set(categoryName, currentCount + 1)
      })
      const commonIssues = Array.from(categoryMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6) // Top 6 categories
      
      setStats({
        totalTickets,
        openTickets,
        inProgressTickets,
        resolvedTickets,
        closedTickets,
        myTickets,
        assignedToMe,
        pendingCustomer,
        highPriority,
        urgentTickets,
        avgResolutionTime: '4.2 hours',
        slaCompliance,
        agentPerformance,
        trendsOverTime,
        userActivity,
        commonIssues
      })
      
    } catch (err: any) {
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-page px-3 py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-1">
            <i className="fas fa-chart-line me-2 text-primary"></i>
            Dashboard
          </h2>
          <p className="text-muted mb-0 small">Welcome back, {user?.name || 'User'}! Here's your ticket overview</p>
        </div>
        <div className="d-flex gap-2">
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => fetchDashboardData()}
          >
            <i className="fas fa-sync me-1"></i>
            <span className="d-none d-sm-inline">Refresh</span>
          </Button>
          {user?.role !== 'ADMIN' && (
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => navigate('/tickets/new')}
              className="d-flex align-items-center"
            >
              <i className="fas fa-plus me-1"></i>
              <span className="d-none d-sm-inline">New Ticket</span>
            </Button>
          )}
        </div>
      </div>

      {/* Charts Section - Top Row */}
      <Row className="g-2 mb-3">
        {/* Ticket Status Bar Chart */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-chart-bar me-2 text-primary"></i>
                Status Overview
              </h6>
            </Card.Header>
            <Card.Body className="p-2">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={[
                    { name: 'Open', count: stats?.openTickets || 0, fill: '#ffc107' },
                    { name: 'Progress', count: stats?.inProgressTickets || 0, fill: '#0dcaf0' },
                    { name: 'Pending', count: stats?.pendingCustomer || 0, fill: '#6c757d' },
                    { name: 'Resolved', count: stats?.resolvedTickets || 0, fill: '#198754' },
                    { name: 'Closed', count: stats?.closedTickets || 0, fill: '#212529' },
                  ]}
                  margin={{ top: 5, right: 5, left: -25, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="count" fill="#667eea" radius={[6, 6, 0, 0]}>
                    {[
                      { name: 'Open', count: stats?.openTickets || 0, fill: '#ffc107' },
                      { name: 'Progress', count: stats?.inProgressTickets || 0, fill: '#0dcaf0' },
                      { name: 'Pending', count: stats?.pendingCustomer || 0, fill: '#6c757d' },
                      { name: 'Resolved', count: stats?.resolvedTickets || 0, fill: '#198754' },
                      { name: 'Closed', count: stats?.closedTickets || 0, fill: '#212529' },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Status Distribution Pie Chart */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-chart-pie me-2 text-primary"></i>
                Status Distribution
              </h6>
            </Card.Header>
            <Card.Body className="p-2">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Open', value: stats?.openTickets || 0, fill: '#ffc107' },
                      { name: 'In Progress', value: stats?.inProgressTickets || 0, fill: '#0dcaf0' },
                      { name: 'Pending', value: stats?.pendingCustomer || 0, fill: '#6c757d' },
                      { name: 'Resolved', value: stats?.resolvedTickets || 0, fill: '#198754' },
                      { name: 'Closed', value: stats?.closedTickets || 0, fill: '#212529' },
                    ].filter(item => item.value > 0)}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={55}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      { name: 'Open', value: stats?.openTickets || 0, fill: '#ffc107' },
                      { name: 'In Progress', value: stats?.inProgressTickets || 0, fill: '#0dcaf0' },
                      { name: 'Pending', value: stats?.pendingCustomer || 0, fill: '#6c757d' },
                      { name: 'Resolved', value: stats?.resolvedTickets || 0, fill: '#198754' },
                      { name: 'Closed', value: stats?.closedTickets || 0, fill: '#212529' },
                    ].filter(item => item.value > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={30}
                    iconType="circle"
                    wrapperStyle={{ fontSize: '11px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Priority Distribution Pie Chart */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-chart-pie me-2 text-primary"></i>
                Priority Distribution
              </h6>
            </Card.Header>
            <Card.Body className="p-2">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Low', value: Math.max(stats?.totalTickets ? Math.round(stats.totalTickets - (stats.highPriority || 0) - (stats.urgentTickets || 0)) : 0, 0), fill: '#198754' },
                      { name: 'High', value: stats?.highPriority || 0, fill: '#ffc107' },
                      { name: 'Urgent', value: stats?.urgentTickets || 0, fill: '#dc3545' },
                    ].filter(item => item.value > 0)}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={55}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      { name: 'Low', value: Math.max(stats?.totalTickets ? Math.round(stats.totalTickets - (stats.highPriority || 0) - (stats.urgentTickets || 0)) : 0, 0), fill: '#198754' },
                      { name: 'High', value: stats?.highPriority || 0, fill: '#ffc107' },
                      { name: 'Urgent', value: stats?.urgentTickets || 0, fill: '#dc3545' },
                    ].filter(item => item.value > 0).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      fontSize: '12px'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={30}
                    iconType="circle"
                    wrapperStyle={{ fontSize: '11px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Agent Performance and Trends */}
      <Row className="g-2 mb-3">
        {/* Agent Performance Bar Chart */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-users me-2 text-primary"></i>
                Agent Performance
              </h6>
            </Card.Header>
            <Card.Body className="p-2">
              {stats?.agentPerformance && stats.agentPerformance.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                    data={stats.agentPerformance}
                    margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        fontSize: '12px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="tickets" 
                      stroke="#667eea" 
                      strokeWidth={3}
                      dot={{ fill: '#667eea', r: 5 }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-user-slash mb-2" style={{ fontSize: '1.5rem', opacity: 0.3 }}></i>
                  <p className="mb-0 small">No agent data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Trends Over Time Pie Chart */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-chart-line me-2 text-primary"></i>
                Trends Over Time (Last 7 Days)
              </h6>
            </Card.Header>
            <Card.Body className="p-2">
              {stats?.trendsOverTime && stats.trendsOverTime.some(t => t.value > 0) ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={stats.trendsOverTime.filter(t => t.value > 0)}
                      cx="50%"
                      cy="40%"
                      labelLine={false}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={55}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {stats.trendsOverTime.filter(t => t.value > 0).map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 360 / 7}, 70%, 60%)`} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        fontSize: '12px'
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={30}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '11px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-chart-line mb-2" style={{ fontSize: '1.5rem', opacity: 0.3 }}></i>
                  <p className="mb-0 small">No trend data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* User Activity and Common Issues */}
      <Row className="g-2 mb-3">
        {/* User Activity Bar Chart */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-user-clock me-2 text-primary"></i>
                User Activity
              </h6>
            </Card.Header>
            <Card.Body className="p-2">
              {stats?.userActivity && stats.userActivity.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={stats.userActivity}
                    margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        fontSize: '12px'
                      }}
                    />
                    <Bar dataKey="tickets" fill="#10b981" radius={[6, 6, 0, 0]}>
                      {stats.userActivity.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${160 + index * 25}, 70%, 50%)`} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-user-clock mb-2" style={{ fontSize: '1.5rem', opacity: 0.3 }}></i>
                  <p className="mb-0 small">No user activity data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Common Issues Pie Chart */}
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-exclamation-circle me-2 text-primary"></i>
                Common Issues
              </h6>
            </Card.Header>
            <Card.Body className="p-2">
              {stats?.commonIssues && stats.commonIssues.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={stats.commonIssues}
                      cx="50%"
                      cy="40%"
                      labelLine={false}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={55}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {stats.commonIssues.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 60%)`} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        fontSize: '12px'
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={30}
                      iconType="circle"
                      wrapperStyle={{ fontSize: '11px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="fas fa-exclamation-circle mb-2" style={{ fontSize: '1.5rem', opacity: 0.3 }}></i>
                  <p className="mb-0 small">No issue data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-2">
        {/* Quick Actions */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-bolt me-2"></i>
                Quick Actions
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="d-flex flex-wrap gap-2">
                {user?.role !== 'ADMIN' && (
                  <Button 
                    variant="primary"
                    size="sm"
                    onClick={() => navigate('/tickets/new')}
                  >
                    <i className="fas fa-plus me-2"></i>
                    New Ticket
                    </Button>
                )}
                
                <Button 
                  variant="outline-primary"
                  size="sm"
                  onClick={() => navigate(
                    user?.role === 'ADMIN' ? '/tickets' : 
                    user?.role === 'REQUESTER' ? '/tickets/my' : 
                    '/tickets/agent'
                  )}
                >
                  <i className="fas fa-list me-2"></i>
                  All Tickets
                    </Button>
                
                {stats && stats.openTickets > 0 && (
                  <Button 
                    variant="outline-warning"
                    size="sm"
                    onClick={() => navigate('/tickets?status=OPEN')}
                  >
                    <i className="fas fa-folder-open me-2"></i>
                    Open ({stats.openTickets})
                    </Button>
                )}
                
                {stats && stats.urgentTickets > 0 && (
                  <Button 
                    variant="outline-danger"
                    size="sm"
                    onClick={() => navigate('/tickets?priority=Urgent')}
                  >
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Urgent ({stats.urgentTickets})
                    </Button>
                )}
                
                {user?.role === 'ADMIN' && (
                  <Button 
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => navigate('/admin')}
                  >
                    <i className="fas fa-cog me-2"></i>
                    Settings
                    </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Performance & Summary */}
        <Col lg={4}>
          {/* Performance Metrics */}
          <Card className="border-0 shadow-sm mb-2">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-chart-pie me-2"></i>
                Performance
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="mb-2">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-muted">Avg Resolution Time</small>
                  <small className="fw-semibold">{stats?.avgResolutionTime || 'N/A'}</small>
                </div>
                <ProgressBar now={75} variant="success" style={{ height: '6px' }} />
                  </div>

              <div className="mb-2">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-muted">SLA Compliance</small>
                  <small className="fw-semibold">{stats?.slaCompliance || 0}%</small>
                </div>
                <ProgressBar 
                  now={stats?.slaCompliance || 0} 
                  variant={stats && stats.slaCompliance > 80 ? 'success' : 'warning'} 
                  style={{ height: '6px' }} 
                />
              </div>

              <div className="mb-0">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-muted">Resolution Rate</small>
                  <small className="fw-semibold">
                    {stats && stats.totalTickets > 0 
                      ? Math.round((stats.resolvedTickets / stats.totalTickets) * 100) 
                      : 0}%
                  </small>
                </div>
                <ProgressBar 
                  now={stats && stats.totalTickets > 0 
                    ? Math.round((stats.resolvedTickets / stats.totalTickets) * 100) 
                    : 0} 
                  variant="info" 
                  style={{ height: '6px' }} 
                />
              </div>
            </Card.Body>
          </Card>

          {/* Ticket Summary */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-2">
              <h6 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Summary
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              <div className="text-center mb-3">
                <div className="display-4 fw-bold text-primary">{stats?.totalTickets || 0}</div>
                <small className="text-muted">Total Tickets</small>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                <small className="text-muted">
                  <i className="fas fa-folder-open me-1 text-warning"></i>
                  Active
                </small>
                <Badge bg="warning" pill>
                  {(stats?.openTickets || 0) + (stats?.inProgressTickets || 0)}
                </Badge>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                <small className="text-muted">
                  <i className="fas fa-check-circle me-1 text-success"></i>
                  Resolved
                </small>
                <Badge bg="success" pill>{stats?.resolvedTickets || 0}</Badge>
                  </div>

              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  <i className="fas fa-times-circle me-1 text-dark"></i>
                  Closed
                </small>
                <Badge bg="dark" pill>{stats?.closedTickets || 0}</Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}
